/**
 * 本地数据的**键布局**与版本号 —— 全部键构造集中在这一个文件里。
 *
 * 为什么键的构造要单独成文件：键不是"顺手拼出来的字符串"，它是数据格式的一部分。
 * 迁移步骤要按旧键找旧记录，读取方要按键前缀拿"最近 N 条"，轮转要按键顺序判断谁最旧。
 * 键一旦散落在各处，改一处忘一处的结果就是"迁移漏了几条记录"或"轮转删错记录"，
 * 而这两种缺陷都只在用户机器上的旧数据里才会暴露。
 *
 * 键的设计规则（每条都对应一个具体用途）：
 *
 * 1. **前缀分集合内子域。** `snap/`、`draft/`、`check/`、`entry/` 之后才是业务键，
 *    这样在一个 object store 里也能用一次 `keys(collection, prefix)` 拿到需要的子集。
 * 2. **时间戳定长补零放在键里**（13 位毫秒）。IndexedDB 的键序就是字典序，
 *    定长数字补零后字典序 == 时间序，于是"从旧到新"和"最近 N 条"都不需要额外的索引存储：
 *    审计日志轮转要删最旧的、飞前检查记录要按时间倒序列出，直接靠键序就够。
 * 3. **不允许键里出现 `/`**。机型名与固件版本可能含空格、斜杠、非 ASCII（例如
 *    `ArduCopter V4.5.6 (7f9c1a2b)`），直接拼进键会破坏前缀结构，所以一律 `encodeURIComponent`。
 * 4. **参数分片的编号定长 6 位**，于是 `chunk 0`（头部）之后的片按字典序就是按序号。
 *
 * 版本号在此声明唯一来源：数据库 schema 版本（IndexedDB 的 `version`，决定跑哪些迁移步骤）
 * 与记录格式版本（每条记录里的 `format` 字段，防止把旧形状当新形状读）**故意用同一个数字**。
 * 两套版本号需要交叉对照时，出错的方式往往是"schema 升了但记录格式忘了升"，
 * 而两者的演进本来就是一件事：迁移步骤把旧记录改写成新形状。
 */

/** IndexedDB 的 schema 版本，同时也是所有记录的 `format` 字段值 */
import type { PersistenceCollection } from './store';

export const PERSISTENCE_SCHEMA_VERSION = 3;

/** 记录里的格式标记。读取方用它与当前值比对，不认识的一律跳过并报告，不做"尽力解读" */
export const RECORD_FORMAT = PERSISTENCE_SCHEMA_VERSION;

/**
 * 记录的格式是否可读：**当前或更早**的格式都读，更新的格式一律跳过。
 *
 * 为什么不是"必须等于当前格式"：迁移只在版本号变化时跑一次，而迁移**可能有意不碰某些记录**
 * （例如审计条目是追加式的、历史条目的形状自 v1 起就没变，改写它们既没必要也违背"不许原地
 * 修改历史"）。用等号判定会让这些记录在升级后全部读不出来 —— 那正是我们承诺不会发生的事。
 * 反过来，比当前程序更新的记录必须拒绝解读：用旧代码去读新形状，结果是把新记录写坏。
 */
export function isReadableFormat(value: unknown): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= RECORD_FORMAT;
}

// ---------------------------------------------------------------------------
// 设置
// ---------------------------------------------------------------------------

/** 设置只存一份，键固定 */
export const SETTINGS_KEY = 'app';

// ---------------------------------------------------------------------------
// 参数快照：按「机型 + 固件版本 + 槽位」隔离
// ---------------------------------------------------------------------------

/**
 * 槽位把"上次下载的样子"与"原厂默认"分开。
 * 用同一个槽位存两者会让"与原厂默认对比"永远只比出空集（两边被互相覆盖）。
 */
export type ParamSnapshotSlot = 'last' | 'factory-default';

export const PARAM_SNAPSHOT_SLOTS: readonly ParamSnapshotSlot[] = ['last', 'factory-default'];

export const PARAM_SNAPSHOT_PREFIX = 'snap/';

/** 片索引定长 6 位：256 条/片 × 999999 片，远超过任何机型的参数量 */
const CHUNK_INDEX_DIGITS = 6;

export function paramSnapshotChunkKey(
  model: string,
  firmware: string,
  slot: ParamSnapshotSlot,
  chunkIndex: number,
): string {
  return `${paramSnapshotSlotPrefix(model, firmware, slot)}${chunkIndex.toString().padStart(CHUNK_INDEX_DIGITS, '0')}`;
}

/** 某一个「机型 + 固件 + 槽位」下所有片的共同前缀 */
export function paramSnapshotSlotPrefix(
  model: string,
  firmware: string,
  slot: ParamSnapshotSlot,
): string {
  return `${PARAM_SNAPSHOT_PREFIX}${encodeURIComponent(model)}/${encodeURIComponent(firmware)}/${slot}/`;
}

export interface ParamSnapshotKeyInfo {
  readonly model: string;
  readonly firmware: string;
  readonly slot: ParamSnapshotSlot;
  readonly chunkIndex: number;
}

/** 解析参数快照的键；不是快照键（或槽位不认识）返回 null 而不是抛错 —— 库里可能有别的程序写的键 */
export function parseParamSnapshotKey(key: string): ParamSnapshotKeyInfo | null {
  if (!key.startsWith(PARAM_SNAPSHOT_PREFIX)) return null;
  const rest = key.slice(PARAM_SNAPSHOT_PREFIX.length);
  const parts = rest.split('/');
  if (parts.length !== 4) return null;
  const [encodedModel, encodedFirmware, slot, index] = parts as [string, string, string, string];
  if (!isParamSnapshotSlot(slot)) return null;
  if (!/^\d+$/.test(index)) return null;
  const chunkIndex = Number.parseInt(index, 10);
  if (!Number.isSafeInteger(chunkIndex)) return null;
  let model: string;
  let firmware: string;
  try {
    model = decodeURIComponent(encodedModel);
    firmware = decodeURIComponent(encodedFirmware);
  } catch {
    // 机型名不在我们手里时可能是任意字节，decodeURIComponent 会抛
    return null;
  }
  return { model, firmware, slot, chunkIndex };
}

export function isParamSnapshotSlot(value: unknown): value is ParamSnapshotSlot {
  return value === 'last' || value === 'factory-default';
}

// ---------------------------------------------------------------------------
// 航线草稿
// ---------------------------------------------------------------------------

export const MISSION_DRAFT_PREFIX = 'draft/';

/**
 * "上次编辑的是哪一份"指针。
 *
 * 单独存一条指针而不是靠 `keys()` 里最大的 draftId：草稿的键不带时间，
 * 列表还依赖记录里的 `updatedAt`，遍历全部草稿去挑最新的既慢又会在草稿多起来后变慢。
 * 指针与草稿正文必须在同一个 `batch()` 里写，否则崩溃后指针可能指向一份不存在的草稿，
 * "恢复上次编辑的内容"就会静默失效。
 */
export const MISSION_DRAFT_LAST_KEY = 'index/last';

export function missionDraftKey(draftId: string): string {
  return `${MISSION_DRAFT_PREFIX}${encodeURIComponent(draftId)}`;
}

// ---------------------------------------------------------------------------
// 飞前检查记录
// ---------------------------------------------------------------------------

export const PREFLIGHT_PREFIX = 'check/';

const TIMESTAMP_DIGITS = 13;

/** 键里带检查时间，于是"最近的 N 次检查"直接取键最大的几条 */
export function preflightKey(at: number, id: string): string {
  return `${PREFLIGHT_PREFIX}${timestampDigits(at)}/${encodeURIComponent(id)}`;
}

// ---------------------------------------------------------------------------
// 审计日志
// ---------------------------------------------------------------------------

/**
 * 审计条目一条一个键：`entry/{序号}/{写入者}`。
 *
 * 刻意**不用一个数组整体存**：那样每追加一条都要重写整个数组，一次失败就丢掉全部历史，
 * 而且"追加式、不许原地修改"这条要求在数组结构里根本无法保证。一条一个键之后，
 * 历史条目在结构上就不可改（没有"重写"这个动作），
 * 两个标签页同时写也不会互相覆盖（键里带写入者），代价只是键数量多。
 */
export const AUDIT_ENTRY_PREFIX = 'entry/';

export function auditEntryKey(seq: number, writer: string): string {
  return `${AUDIT_ENTRY_PREFIX}${timestampDigits(seq)}/${encodeURIComponent(writer)}`;
}

/** 审计日志的累计统计（条数 / 估算字节 / 下次可用序号），供轮转判断用 */
export const AUDIT_STATS_KEY = 'stats';

export interface AuditKeyInfo {
  readonly seq: number;
  readonly writer: string;
}

export function parseAuditKey(key: string): AuditKeyInfo | null {
  if (!key.startsWith(AUDIT_ENTRY_PREFIX)) return null;
  const rest = key.slice(AUDIT_ENTRY_PREFIX.length);
  const separator = rest.indexOf('/');
  if (separator <= 0) return null;
  const digits = rest.slice(0, separator);
  if (digits.length !== TIMESTAMP_DIGITS) return null;
  const seq = Number.parseInt(digits, 10);
  if (!Number.isSafeInteger(seq)) return null;
  try {
    return { seq, writer: decodeURIComponent(rest.slice(separator + 1)) };
  } catch {
    return null;
  }
}

function timestampDigits(value: number): string {
  const clamped = Math.max(0, Math.floor(value));
  return clamped.toString().padStart(TIMESTAMP_DIGITS, '0');
}

// ---------------------------------------------------------------------------
// 元数据
// ---------------------------------------------------------------------------

/** 数据库自述记录：写在 meta 集合里，用于在"版本不认识"时给出可读原因 */
export const SCHEMA_META_KEY = 'schema';

/**
 * 上一次迁移留下的问题清单。
 *
 * 必须持久化：迁移只在版本号变化时跑一次，跑完后那些没能迁移的旧记录**仍然原样留在库里**，
 * 而下次打开不会重跑迁移。不落盘的话，用户刷新一次页面就再也看不到"有几条旧记录没迁移"
 * 这个事实，只会看到数据缺失。
 */
export const MIGRATION_ISSUES_KEY = 'migration-issues';

/** 参数元数据（`apm.pdef.json` 原文）缓存所在集合 */
export const PARAM_METADATA_COLLECTION: PersistenceCollection = 'meta';

/**
 * 参数元数据缓存的键前缀。
 *
 * 元数据缓存刻意**不另开一个数据库**：两个库就是两套版本号与两条迁移路径
 * （曾经就是 `hgc-param-metadata` v1 与 `hgc` v3），升级存储时必然有一边被漏掉，
 * 而漏掉的表现是"中文说明有时在、有时不在"—— 没有任何报错，也几乎不可能被用户描述清楚。
 * 现在它只是本库里 `meta` 集合下一个带前缀的子域，和参数快照、审计日志共用同一条迁移链。
 *
 * 前缀之后接的是 `cacheKeyOf()` 的结果（`机型@固件版本`），**不在这里重新拼一遍键**：
 * 键格式只有一个出处，前缀是常量，两处不可能分叉。因此这个前缀也只被当作前缀使用
 * （列出时整段剥掉），后面的内容一概不解析。
 */
export const PARAM_METADATA_CACHE_PREFIX = 'pdef/';

// ---------------------------------------------------------------------------
// 读取辅助：库里读出来的东西一律当作不可信输入
// ---------------------------------------------------------------------------

/**
 * 判断是否"形如普通对象的记录"。
 * 用鸭子类型而不是 `instanceof Object`：结构化克隆出来的对象原型一定是 `Object.prototype`，
 * 但跨实现（fake-indexeddb 返回的包装对象）时 `instanceof` 会给出意外的结果。
 */
export function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function readString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function readFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function readBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/** 可选字符串字段的读取辅助：非字符串一律当"没有"，避免把 `undefined` 混进记录里 */
export function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
